import { Icon } from "@/shared/ui/icon"
import type { ReactNode } from "react"

export type AtlasAttachment = {
  name: string
  meta: string
  icon?: string
}

export type AtlasToolCall = {
  label: string
  value: string
  icon?: string
  status?: "connected" | "done" | "draft"
}

export type AtlasChatStep = {
  title: string
  detail?: string
  source?: string
  time?: string
  model?: string
  toolCalls?: readonly AtlasToolCall[]
}

export type AtlasRunStatus = "idle" | "running" | "complete"

const statusLabels: Record<AtlasRunStatus, string> = {
  idle: "Готов к запуску",
  running: "Выполняю",
  complete: "Готово",
}

function AtlasMark({ children = "wJ" }: { children?: string }) {
  return (
    <span className="hidden size-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-2xs font-semibold text-slate-800 shadow-[inset_0_1px_0_rgb(255_255_255/0.75)] dark:bg-[#3b414b] dark:text-white dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.18)] sm:flex">
      {children}
    </span>
  )
}

function AgentRunIcon() {
  return (
    <span className="flex size-9 shrink-0 items-center justify-center rounded-[0.9rem] bg-[#07142d] shadow-[inset_0_1px_0_rgb(255_255_255/0.12)] dark:bg-white sm:size-10">
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="size-5.5 dark:hidden"
        fill="none"
      >
        <rect
          x="4.5"
          y="3.75"
          width="15"
          height="16.5"
          rx="3.25"
          stroke="#ffffff"
          strokeWidth="2"
        />
        <path
          d="m8 9 1.25 1.25L11.5 8"
          stroke="#ffffff"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="m8 15 1.25 1.25L11.5 14"
          stroke="#ffffff"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M13.5 9.25h3" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
        <path d="M13.5 15.25h3" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="hidden size-5.5 dark:block"
        fill="none"
      >
        <rect
          x="4.5"
          y="3.75"
          width="15"
          height="16.5"
          rx="3.25"
          stroke="#07142d"
          strokeWidth="1.8"
        />
        <path
          d="m8 9 1.25 1.25L11.5 8"
          stroke="#07142d"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="m8 15 1.25 1.25L11.5 14"
          stroke="#07142d"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M13.5 9.25h3" stroke="#07142d" strokeWidth="1.8" strokeLinecap="round" />
        <path
          d="M13.5 15.25h3"
          stroke="#07142d"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    </span>
  )
}

export function AtlasUserMessage({
  children,
  time = "12:45",
  attachments = [],
  author = "Пользователь",
  avatar = "U",
}: {
  children: ReactNode
  time?: string
  attachments?: readonly AtlasAttachment[]
  author?: string
  avatar?: string
}) {
  return (
    <div className="atlas-user-message flex flex-col items-end gap-2">
      <div className="flex items-center gap-2 text-xs">
        <span className="font-semibold text-slate-950 dark:text-white">{author}</span>
        <span className="font-mono text-2xs text-[#66718a]">{time}</span>
        <span className="atlas-user-avatar flex size-6 items-center justify-center rounded-full bg-[#3b414b] text-2xs font-semibold text-white">
          {avatar}
        </span>
      </div>
      <div className="max-w-[92%] rounded-[1.125rem] rounded-tr-md border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-medium text-slate-950 shadow-[inset_0_1px_0_rgb(255_255_255/0.7)] dark:border-white/[0.07] dark:bg-[#22252d] dark:text-white dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.03)] sm:max-w-[86%] sm:px-5 sm:text-base">
        {children}
        {attachments.length > 0 ? (
          <ul className="mt-3 grid gap-2">
            {attachments.map((attachment) => (
              <li
                key={attachment.name}
                className="grid grid-cols-[auto_minmax(0,1fr)] gap-2 rounded-xl border border-slate-200 bg-white p-2.5 dark:border-white/10 dark:bg-white/[0.055]"
              >
                <span className="flex size-8 items-center justify-center rounded-lg bg-slate-100 text-[#ff8a2a] dark:bg-white/10">
                  <Icon
                    name={attachment.icon ?? "document-text-linear"}
                    className="size-4"
                  />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-xs font-semibold text-slate-950 dark:text-white">
                    {attachment.name}
                  </span>
                  <span className="block truncate font-mono text-2xs text-slate-500 dark:text-white/45">
                    {attachment.meta}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  )
}

export function AtlasPresenceBar({
  label,
  names,
}: {
  label: string
  names: readonly string[]
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.035] sm:px-4">
      <div className="flex -space-x-2">
        {names.map((name) => (
          <span
            key={name}
            className="flex size-7 items-center justify-center rounded-full border-2 border-white bg-[#3b414b] text-2xs font-semibold text-white dark:border-[#171717]"
            title={name}
          >
            {name.slice(0, 1)}
          </span>
        ))}
      </div>
      <p className="min-w-0 text-xs font-medium text-slate-700 dark:text-white/70">
        {label}
      </p>
    </div>
  )
}

export function AtlasAgentRun({
  steps,
  status = "complete",
  meta,
  action,
  enabledSteps,
}: {
  steps: readonly AtlasChatStep[]
  status?: AtlasRunStatus
  meta?: string
  action?: ReactNode
  enabledSteps?: number
}) {
  const activeSteps = enabledSteps ?? (status === "idle" ? 0 : steps.length)

  return (
    <div className="atlas-agent-run mt-5 flex items-start gap-2 sm:mt-6 sm:gap-3">
      <AtlasMark />
      <section className="min-w-0 flex-1 overflow-hidden rounded-[1.125rem] border border-slate-200 bg-white text-slate-950 dark:border-white/10 dark:bg-[#242424] dark:text-white">
        <header className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-3 border-b border-slate-200 px-4 py-4 dark:border-white/10 sm:grid-cols-[auto_minmax(0,1fr)_auto_auto] sm:items-center sm:px-5">
          <AgentRunIcon />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold leading-none sm:text-base">
                Ход агента
              </p>
              <span
                className={`text-xs font-medium ${
                  status === "complete"
                    ? "text-emerald-600 dark:text-emerald-300"
                    : status === "running"
                      ? "text-violet-600 dark:text-violet-300"
                      : "text-slate-400 dark:text-white/40"
                }`}
              >
                {statusLabels[status]}
              </span>
            </div>
            {meta ? (
              <p className="mt-1 max-w-full truncate font-mono text-2xs text-slate-400 dark:text-[#6d7280]">
                {meta}
              </p>
            ) : null}
          </div>
          {action ? (
            <div className="col-span-3 justify-self-end sm:col-auto sm:col-start-3 sm:row-start-1">
              {action}
            </div>
          ) : null}
          <Icon
            name="alt-arrow-up-linear"
            className="col-start-3 row-start-1 size-4 shrink-0 justify-self-end text-slate-400 dark:text-[#8da0ba] sm:col-start-4"
          />
        </header>

        <ol className="px-4 py-3 sm:px-5">
          {steps.map((step, index) => {
            const isEnabled = index < activeSteps

            return (
              <li
                key={`${step.title}-${index}`}
                className={`grid grid-cols-[0.75rem_minmax(7.5rem,1fr)_auto] gap-x-2 py-3 first:pt-1 transition-opacity duration-300 motion-reduce:transition-none sm:grid-cols-[1rem_minmax(0,1fr)_auto] sm:gap-x-3 ${
                  isEnabled ? "opacity-100" : "opacity-30"
                }`}
              >
                <span className="mt-1.5 flex size-3 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500 dark:bg-emerald-400/25 dark:text-emerald-300">
                  <Icon name="check-read-linear" className="size-2" />
                </span>
                <div className="min-w-0">
                  <p className="text-2xs font-medium text-slate-400 dark:text-[#9da6ba]">
                    Шаг {index + 1}
                  </p>
                  <p className="mt-1.5 text-sm font-semibold text-slate-950 dark:text-white">
                    {step.title}
                  </p>
                  {step.detail ? (
                    <p className="mt-1 text-xs text-slate-600 dark:text-white/80">
                      {step.detail}
                    </p>
                  ) : null}
                  {step.toolCalls?.length ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {step.toolCalls.map((tool) => (
                        <span
                          key={`${step.title}-${tool.label}-${tool.value}`}
                          className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-transparent bg-transparent px-0 py-0 font-mono text-2xs leading-none text-slate-500 dark:border-white/10 dark:bg-white/[0.06] dark:px-2.5 dark:py-1.5 dark:text-white/65"
                        >
                          <Icon
                            name={tool.icon ?? "widget-2-bold-duotone"}
                            className="size-3.5 shrink-0 text-emerald-500 dark:text-emerald-300"
                          />
                          <span className="truncate">{tool.label}</span>
                          <span className="text-slate-300 dark:text-white/30">/</span>
                          <span className="truncate text-slate-600 dark:text-white/85">
                            {tool.value}
                          </span>
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div className="ml-1 flex flex-col items-end gap-2">
                  {step.time ? (
                    <span className="font-mono text-2xs text-slate-400 dark:text-[#687080]">
                      {step.time}
                    </span>
                  ) : null}
                </div>
              </li>
            )
          })}
        </ol>
      </section>
    </div>
  )
}

export function AtlasInputBar() {
  return (
    <div className="atlas-input-bar rounded-[1.35rem] border border-[#3a4150] bg-[#242424] px-4 py-3 shadow-[inset_0_1px_0_rgb(255_255_255/0.04)] transition-[border-color,box-shadow] duration-300 focus-within:border-orange-500 focus-within:shadow-[0_0_0_1px_rgb(249_115_22/0.55),inset_0_1px_0_rgb(255_255_255/0.04)] sm:rounded-[1.4rem] sm:py-3.5">
      <div className="grid min-h-16 grid-rows-[1fr_auto] gap-2.5 sm:min-h-18 sm:gap-3">
        <p className="line-clamp-2 min-h-9 min-w-0 overflow-hidden text-sm font-medium text-[#aab4c7] sm:min-h-10">
          Спросите что-нибудь...
        </p>
        <div className="flex items-center gap-2.5 sm:gap-3">
          <button
            type="button"
            aria-label="Прикрепить файл"
            className="flex size-11 shrink-0 items-center justify-center text-[#aab4c7] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
          >
            <Icon name="paperclip-linear" className="size-4.5" />
          </button>
          <button
            type="button"
            className="hidden shrink-0 items-center gap-1.5 text-xs font-medium text-white hover:text-white/80 sm:flex"
          >
            <span>Высокий</span>
            <Icon name="alt-arrow-down-linear" className="size-3 text-[#aab4c7]" />
          </button>
          <button
            type="button"
            aria-label="Отправить"
            className="ml-auto flex size-11 shrink-0 items-center justify-center rounded-full bg-[#777777] text-[#181818] transition-colors hover:bg-[#9a9a9a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
          >
            <Icon name="arrow-up-linear" className="size-4 sm:size-4.5" />
          </button>
        </div>
      </div>
    </div>
  )
}
